import { useNavigate } from 'react-router-dom'

import { Box, Spinner, Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import { ItemIntegrationFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { useGetMarketplaceCompanyInstallations } from 'modules/marketplace/hooks/integrations/companyInstallations/useGetMarketplaceCompanyInstallations'
import { type WizardData } from 'modules/marketplace/lib/types'
import { useCompanyInstallations } from 'modules/marketplace/store/collections/companyInstallations'

type SettingsWizardProps = {
  integration: ItemIntegrationFragment
  steps: WizardStep<WizardData>[]
  initialData: WizardData
}

const SettingsWizard = ({
  steps,
  initialData,
  integration: { id, datoId, name },
}: SettingsWizardProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const companyInstallations = useCompanyInstallations()
  const session = useCurrentSession()
  const { data: companyInstallationsData, loading: loadingCompanyInstallations } =
    useGetMarketplaceCompanyInstallations(
      {
        // FIXME: We should arrange the components to call this hook only when there is an id
        integrationIds: [id ?? '0'],
      },
      false
    )

  const handleSubmit = async (data: WizardData) => {
    if (companyInstallationsData.length > 0) {
      handleInstallationEdition(data.answers)
    } else {
      handleInstallation(data.answers)
    }
  }

  const handleInstallation = async (data: { [key: string]: string | number | boolean }) => {
    const payload = {
      access_id: session.access.id,
      form_data: data,
      company_id: session.company.id,
      integration_id: id,
    }

    const { success } = await asyncAction(
      async () => {
        await companyInstallations.create(payload)
      },
      { loadingMessage: i18n.t('common.loading'), successFlash: true }
    )
    if (success) {
      navigate(navigation.discover.marketplace.item.fullPath({ id: datoId }))
      track(trackingEvents.marketplace.integrationInstalledSuccesfully, {
        integration_id: id,
        company_id: session.company.id,
      })
    }
  }

  const handleInstallationEdition = async (data: { [key: string]: string | number | boolean }) => {
    await companyInstallations.fetch()
    const companyInstallation = companyInstallations.find({
      integration_id: id,
    })

    const newData = JSON.parse(JSON.stringify(data))

    const { success } = await asyncAction(
      async () => {
        await companyInstallation?.update({
          integration_id: id || undefined,
          form_data: newData,
          company_id: session.company.id,
          access_id: session.access.id,
        })
      },
      { loadingMessage: i18n.t('common.loading'), successFlash: true }
    )
    if (success) {
      navigate(navigation.discover.marketplace.item.fullPath({ id: datoId }))
      track(trackingEvents.marketplace.integrationUpdatedSuccesfully, {
        integration_id: id,
        company_id: session.company.id,
      })
    }
  }

  return (
    <Box height="full">
      {loadingCompanyInstallations ? (
        <Spinner />
      ) : (
        <Wizard.Base
          title={name}
          steps={steps}
          initialData={initialData}
          responsive
          onSubmit={(data) => {
            handleSubmit(data)
          }}
        />
      )}
    </Box>
  )
}

export default SettingsWizard
